<script lang="ts" setup>
import { inject, onMounted, onUnmounted, ref } from 'vue'
import type { Ref } from 'vue'
import { brandConfig, features } from '@/config/brands'
import { createDebugger } from '@/utils/debug'

const props = defineProps<{
  adSlotIndex: number
  adInstanceId: string
  adOrder: number
  chapterId: string
}>()

const config = brandConfig.adSense
const slot = config?.slots[props.adSlotIndex % config.slots.length]
const enabled = features?.enableAdSense && !!config?.client && !!slot
const containerRef = ref<HTMLElement>()
const adRef = ref<HTMLElement>()
const readerContainerRef = inject<Ref<HTMLElement | undefined>>('containerRef')
const debug = createDebugger('ad')
let observer: IntersectionObserver | undefined
let requested = false
let statusObserver: MutationObserver | undefined
let resultTimer: ReturnType<typeof setTimeout> | undefined
let scriptElement: HTMLScriptElement | undefined
let lastStatus = ''
let lastProcessingStatus = ''
const logContext = `chapterId=${props.chapterId} adOrder=${props.adOrder} slot=${slot} instance=${props.adInstanceId}`

function clearResultTimer() {
  if (resultTimer !== undefined)
    clearTimeout(resultTimer)
  resultTimer = undefined
}

function readAdStatus() {
  const status = adRef.value?.getAttribute('data-ad-status') || ''
  const processingStatus = adRef.value?.getAttribute('data-adsbygoogle-status') || ''
  if (processingStatus && processingStatus !== lastProcessingStatus) {
    lastProcessingStatus = processingStatus
    debug(`AdSense SDK 处理状态=${processingStatus}（不代表广告已填充） ${logContext}`)
  }
  if (!status || status === lastStatus)
    return
  lastStatus = status
  clearResultTimer()
  if (status === 'filled')
    debug(`AdSense 填充结果=filled（已返回广告） ${logContext}`)
  else if (status === 'unfilled')
    debug.warn(`AdSense 填充结果=unfilled（未返回广告，具体原因需查看 AdSense 后台） ${logContext}`)
  else
    debug(`AdSense 广告状态=${status} ${logContext}`)
}

function onScriptLoad() {
  debug(`AdSense SDK 脚本加载完成（不代表广告已填充） ${logContext}`)
}

function onScriptError() {
  clearResultTimer()
  debug.warn(`AdSense SDK 脚本加载失败，请检查网络或广告拦截 ${logContext}`)
}

function requestAd() {
  const el = containerRef.value
  if (requested || !enabled || !el || el.getBoundingClientRect().width <= 0)
    return

  debug(`AdSense 广告位进入可见范围，准备请求 client=${config!.client} ${logContext}`)
  const scriptId = 'reader-adsense-script'
  let script = document.getElementById(scriptId) as HTMLScriptElement | null
  const needsScript = !script
  if (!script) {
    script = document.createElement('script')
    script.id = scriptId
    script.async = true
    script.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${config!.client}`
    script.crossOrigin = 'anonymous'
    script.dataset.readerLoadStatus = 'loading'
    script.addEventListener('load', () => {
      script!.dataset.readerLoadStatus = 'loaded'
    }, { once: true })
    script.addEventListener('error', () => {
      script!.dataset.readerLoadStatus = 'error'
    }, { once: true })
  }
  scriptElement = script
  script.addEventListener('load', onScriptLoad)
  script.addEventListener('error', onScriptError)

  statusObserver = new MutationObserver(readAdStatus)
  if (adRef.value)
    statusObserver.observe(adRef.value, { attributes: true, attributeFilter: ['data-ad-status', 'data-adsbygoogle-status'] })
  resultTimer = setTimeout(() => {
    debug.warn(`AdSense 等待填充结果超过 15 秒（状态未知，不判定为无填充或审核失败） sdk=${scriptElement?.dataset.readerLoadStatus || 'unknown'} processing=${adRef.value?.getAttribute('data-adsbygoogle-status') || 'pending'} ${logContext}`)
  }, 15000)
  if (needsScript) {
    debug(`AdSense SDK 开始加载 ${logContext}`)
    document.head.appendChild(script)
  }
  else if (script.dataset.readerLoadStatus === 'loaded') {
    onScriptLoad()
  }
  else if (script.dataset.readerLoadStatus === 'error') {
    onScriptError()
  }

  requested = true
  observer?.disconnect()
  try {
    const adWindow = window as Window & { adsbygoogle?: { push: (value: Record<string, never>) => void } }
    adWindow.adsbygoogle = adWindow.adsbygoogle || []
    adWindow.adsbygoogle.push({})
    debug(`AdSense 广告请求已提交（等待填充结果） ${logContext}`)
    readAdStatus()
  }
  catch (error) {
    clearResultTimer()
    debug.warn(`AdSense 广告请求提交失败 ${logContext}`, error)
  }
}

onMounted(() => {
  if (!enabled || !containerRef.value)
    return
  observer = new IntersectionObserver((entries) => {
    if (entries.some(entry => entry.isIntersecting))
      requestAd()
  }, { root: readerContainerRef?.value || null })
  observer.observe(containerRef.value)
})

onUnmounted(() => {
  observer?.disconnect()
  statusObserver?.disconnect()
  clearResultTimer()
  scriptElement?.removeEventListener('load', onScriptLoad)
  scriptElement?.removeEventListener('error', onScriptError)
})
</script>

<template>
  <div v-if="enabled" ref="containerRef" class="chapter-ad-adsense">
    <ins
      ref="adRef"
      class="adsbygoogle"
      style="display: block"
      :data-ad-client="config.client"
      :data-ad-slot="slot"
      data-ad-format="auto"
      data-full-width-responsive="true"
    />
  </div>
</template>

<style scoped>
.chapter-ad-adsense {
  width: 100%;
  min-height: 1px;
  padding: 8px 0;
}
</style>
